import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { X, Loader2, Mail, Phone, Globe, Save } from 'lucide-react';
import { salesApi, formatMoney, formatMoneyShort, serverError } from '../../lib/salesApi';
import { StatusPill } from './SaleDetailModal';

const CY = '#90E0EF';

function fmtDate(d) { return d ? new Date(d).toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' }) : '—'; }

export default function ClientProfileDrawer({ clientId, open, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [note, setNote] = useState('');
  const [savingNote, setSavingNote] = useState(false);

  useEffect(() => {
    if (!open || !clientId) return;
    setLoading(true);
    salesApi.client(clientId)
      .then((d) => { setData(d); setNote(d.client.internal_note || ''); })
      .catch((e) => toast.error(serverError(e)))
      .finally(() => setLoading(false));
  }, [open, clientId]);

  if (!open) return null;
  const c = data?.client;

  async function saveNote() {
    setSavingNote(true);
    try {
      await salesApi.updateClient(clientId, { internal_note: note });
      toast.success('Note saved');
    } catch (e) { toast.error(serverError(e)); } finally { setSavingNote(false); }
  }

  return (
    <AnimatePresence>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[110] bg-black/70 backdrop-blur-sm flex justify-end admin-root" onClick={onClose} data-testid="client-profile-drawer">
        <motion.div initial={{ x: 40, opacity: 0 }} animate={{ x: 0, opacity: 1 }} exit={{ x: 40, opacity: 0 }}
          transition={{ type: 'tween', duration: 0.2 }} onClick={(e) => e.stopPropagation()}
          className="w-full max-w-lg h-full bg-[#080808] border-l border-[#1a1a1a] overflow-y-auto">
          <div className="sticky top-0 flex items-center justify-between px-6 h-14 border-b border-[#161616] bg-[#080808]">
            <span className="font-outfit font-semibold text-white">Collector</span>
            <button onClick={onClose} style={{ cursor: 'pointer' }} className="text-[#888] hover:text-white"><X className="w-5 h-5" /></button>
          </div>

          {loading || !c ? (
            <div className="flex items-center justify-center h-40"><Loader2 className="w-5 h-5 animate-spin" style={{ color: CY }} /></div>
          ) : (
            <div className="p-6">
              <h2 className="font-outfit font-bold text-white text-2xl tracking-tight uppercase" data-testid="client-name">{c.name || 'Unnamed collector'}</h2>
              <p className="text-[11px] font-mono text-[#777] mt-1">Collector since {fmtDate(c.first_purchase_at || c.created_at)}</p>
              <div className="mt-2 flex flex-col gap-1 text-xs font-mono text-[#aaa]">
                <span className="inline-flex items-center gap-2"><Mail className="w-3 h-3 text-[#555]" /> {c.email}</span>
                {c.phone && <span className="inline-flex items-center gap-2"><Phone className="w-3 h-3 text-[#555]" /> {c.phone}</span>}
                {c.country && <span className="inline-flex items-center gap-2"><Globe className="w-3 h-3 text-[#555]" /> {c.country}</span>}
              </div>

              <div className="mt-6 grid grid-cols-3 gap-3">
                <Stat label="Total Purchased" value={formatMoneyShort(c.total_purchased_minor, c.default_currency)} accent />
                <Stat label="Purchases" value={c.purchase_count} />
                <Stat label="Last Purchase" value={c.last_purchase_at ? fmtDate(c.last_purchase_at) : '—'} />
              </div>

              <div className="mt-4 flex items-center justify-between border border-[#1a1a1a] px-3 py-2.5">
                <span className="text-[10px] font-mono uppercase tracking-[0.14em] text-[#666]">Marketing</span>
                <span className="text-[10px] font-mono uppercase tracking-[0.14em]" style={{ color: data.marketing_status === 'subscribed' ? '#7ed4a0' : '#9a9a9a' }}>
                  {data.marketing_status}
                </span>
              </div>

              <div className="mt-6">
                <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#666] mb-3">Purchase History</p>
                <div className="space-y-2" data-testid="client-history">
                  {(data.sales || []).length === 0 && <p className="text-xs font-mono text-[#555]">No sales yet.</p>}
                  {(data.sales || []).map((s) => (
                    <div key={s.id} className="flex items-center justify-between gap-3 border border-[#161616] px-3 py-2.5">
                      <div className="min-w-0">
                        <p className="text-sm text-[#e8e8e8] truncate">{s.item_title}{s.edition ? ` · ${s.edition}` : ''}</p>
                        <p className="text-[10px] font-mono text-[#666] mt-0.5">{s.seller_label}{s.artist_name ? ` · ${s.artist_name}` : ''}</p>
                        {s.cards && s.cards.length > 0 && (
                          <p className="text-[10px] font-mono mt-1" style={{ color: '#90E0EF' }}>{s.cards.map((c) => `#${c}`).join('  ')}</p>
                        )}
                      </div>
                      <div className="text-right shrink-0">
                        <p className="font-mono text-sm text-white">{formatMoney(s.amount_minor, s.currency)}</p>
                        <StatusPill status={s.status} className="mt-1" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-6">
                <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#666] mb-2">Internal Note</p>
                <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} data-testid="client-note-input"
                  className="w-full bg-[#0d0d0d] border border-[#222] text-[#f0f0f0] text-xs font-mono px-3 py-2.5 resize-none focus:outline-none focus:border-[#90E0EF]" />
                <button onClick={saveNote} disabled={savingNote} style={{ cursor: 'pointer' }}
                  className="mt-2 inline-flex items-center gap-2 border border-[#2a2a2a] text-[#ccc] hover:text-white hover:border-[#90E0EF]/50 px-3 py-2 text-[10px] font-mono uppercase tracking-[0.14em] disabled:opacity-50">
                  {savingNote ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Save Note
                </button>
              </div>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className="border border-[#1a1a1a] px-3 py-3">
      <p className="text-[9px] font-mono uppercase tracking-[0.14em] text-[#666]">{label}</p>
      <p className="font-outfit font-semibold mt-1 text-lg" style={{ color: accent ? CY : '#f0f0f0' }}>{value}</p>
    </div>
  );
}
